import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { mvpApi } from '../../../services/api';

const RELATIONSHIP_VALUES = ['Spouse', 'Child', 'Parent', 'Sibling', 'Other'];

function relationshipLabel(value, t) {
  const map = {
    Spouse: 'familyRelSpouse',
    Child: 'familyRelChild',
    Parent: 'familyRelParent',
    Sibling: 'familyRelSibling',
    Other: 'familyRelOther',
  };
  const key = map[value];
  return key ? t(`advisorPortal.${key}`) : value;
}

export default function FamilyMembersCases({
  familyGroup,
  currentCustomerId,
  members = [],
  onFamilyUpdated,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const languageOptions = useMemo(
    () => [
      { value: 'es', label: t('advisorPortal.newCustomerLangEs') },
      { value: 'en', label: t('advisorPortal.newCustomerLangEn') },
      { value: 'fr', label: t('advisorPortal.newCustomerLangFr') },
      { value: 'ar', label: t('advisorPortal.newCustomerLangAr') },
    ],
    [t],
  );

  const familyGroupId = familyGroup?.familyGroupId || familyGroup?.family_group_id || familyGroup?.id || null;

  const filtered = useMemo(
    () => members.filter((m) => String(m.user_id ?? m.id) !== String(currentCustomerId)),
    [members, currentCustomerId],
  );

  const displayFamilyName =
    familyGroup?.familyLabel || familyGroup?.family_name || familyGroup?.familyName || t('advisorPortal.profileFamilyDefaultName');
  const familyCount = members.length;
  const isFull = familyCount >= 10;

  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    relationship: 'Spouse',
    preferredLanguage: 'es',
    mobile: '',
    email: '',
  });

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2200);
  };

  const resetForm = () =>
    setForm({
      firstName: '',
      lastName: '',
      relationship: 'Spouse',
      preferredLanguage: 'es',
      mobile: '',
      email: '',
    });

  const canSubmit = String(form.firstName || '').trim() && String(form.lastName || '').trim() && (String(form.email || '').trim() || String(form.mobile || '').trim());

  const submit = async () => {
    if (!familyGroupId) return;
    if (!canSubmit) return;

    const first_name = String(form.firstName).trim();
    const last_name = String(form.lastName).trim();
    const email = String(form.email || '').trim() || undefined;

    const rawMobile = String(form.mobile || '').trim();
    const phone =
      rawMobile && !rawMobile.startsWith('+')
        ? `+${rawMobile}`
        : rawMobile || undefined;

    setBusy(true);
    try {
      const preferred_contact_method = email ? 'email' : 'mobile';
      const preferred_contact_channel = email ? undefined : 'sms';

      const resp = await mvpApi.createAdminUser({
        first_name,
        last_name,
        email,
        phone,
        preferred_language: form.preferredLanguage,
        preferred_contact_method,
        preferred_contact_channel,
      });

      const newUserId = resp?.data?.user_id ?? resp?.data?.userId;
      if (!newUserId) throw new Error('User creation response missing user_id');

      await mvpApi.addFamilyMember(familyGroupId, newUserId, form.relationship);
      showToast(t('advisorPortal.familyToastAdded'));
      setShowModal(false);
      resetForm();
      await onFamilyUpdated?.();
    } catch {
      showToast(t('advisorPortal.familyToastFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white text-xs font-medium px-4 py-2.5 rounded-xl">
          {toast}
        </div>
      ) : null}

      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100 bg-slate-50/60">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-team-line text-slate-500 text-base" />
        </div>
        <h2 className="text-sm font-semibold text-slate-800">{t('advisorPortal.familyMembersTitle', { name: displayFamilyName })}</h2>
        <div className="flex-1" />
        {familyGroupId ? (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            disabled={isFull}
            className="flex items-center gap-1 text-[11px] font-medium text-white bg-slate-700 px-2.5 py-1 rounded-md hover:bg-slate-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-user-add-line text-xs" />
            {isFull ? t('advisorPortal.familyFull10') : t('advisorPortal.familyAddMember')}
          </button>
        ) : null}
      </div>

      <div className="p-3 space-y-1">
        {filtered.length === 0 ? (
          null
        ) : (
          filtered.map((member) => {
            const memberId = member.user_id ?? member.id;
            return (
              <div key={memberId} className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors">
                <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-slate-500">
                    {(member.name || 'U').slice(0, 1).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate whitespace-nowrap">
                    {member.name || `User ${memberId}`}
                  </p>
                  <p className="text-[11px] text-slate-400 truncate">
                    {member.email || member.phone || t('advisorPortal.familyNoContactInfo')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/customer/${memberId}`)}
                  className="flex items-center gap-1 text-[11px] font-medium text-white bg-slate-700 px-2.5 py-1 rounded-md hover:bg-slate-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {t('advisorPortal.familyOpenProfile')}
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 inline-block shrink-0 ml-0.5 opacity-90" />
                </button>
              </div>
            );
          })
        )}
      </div>

      {showModal ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-5 border border-slate-200">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">{t('advisorPortal.familyModalTitle')}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{t('advisorPortal.familyModalSubtitle')}</p>
              </div>
              <button type="button" onClick={() => { setShowModal(false); resetForm(); }} className="text-xs text-slate-500 hover:text-slate-700">
                <i className="ri-close-line text-base" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t('advisorPortal.familyLabelFirstNameReq')}
                </label>
                <input value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t('advisorPortal.familyLabelLastNameReq')}
                </label>
                <input value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40" />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t('advisorPortal.familyLabelRelationshipReq')}
                </label>
                <select value={form.relationship} onChange={(e) => setForm((p) => ({ ...p, relationship: e.target.value }))} className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40">
                  {RELATIONSHIP_VALUES.map((r) => (
                    <option key={r} value={r}>
                      {relationshipLabel(r, t)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t('advisorPortal.familyLabelPreferredLanguage')}
                </label>
                <select value={form.preferredLanguage} onChange={(e) => setForm((p) => ({ ...p, preferredLanguage: e.target.value }))} className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40">
                  {languageOptions.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t('advisorPortal.familyLabelMobile')}
                </label>
                <input
                  value={form.mobile}
                  onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                  placeholder={t('advisorPortal.familyPhMobile')}
                  className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                  {t('advisorPortal.familyLabelEmail')}
                </label>
                <input
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder={t('advisorPortal.familyPhEmail')}
                  className="mt-1 w-full text-sm border border-slate-200 rounded-lg px-3 py-2 text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm(); }}
                className="text-xs text-slate-500 hover:text-slate-700 px-3 py-2 rounded-lg border border-slate-200"
              >
                {t('advisorPortal.familyModalCancel')}
              </button>
              <button
                type="button"
                disabled={!canSubmit || busy || isFull}
                onClick={submit}
                className="text-xs font-semibold bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? t('advisorPortal.familySaveBusy') : isFull ? t('advisorPortal.familyFullLabel') : t('advisorPortal.familyAddToFamily')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

